import React, { useState, useEffect, useRef, useCallback } from "react";
import { getFeed } from "../api/games";
import GameCard from "./GameCard";
import DownloadModal from "./DownloadModal";
import Reveal from "./Reveal";
import { Button, EmptyState } from "./ui";

const PER_PAGE = 18;

/**
 * Секция «Популярные игры» — сетка карточек из фида Storys (sort=popular).
 * Клик по карточке → модалка «скачай приложение».
 * Кнопка «Показать ещё» подгружает следующую страницу.
 */
export default function GamesSection() {
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [modalGame, setModalGame] = useState(null);
  const abortRef = useRef(null);

  const loadPage = useCallback((pageNum) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(false);

    getFeed({ page: pageNum, perPage: PER_PAGE, sort: "popular" }, ctrl.signal)
      .then(({ projects, total: t }) => {
        setGames((prev) =>
          pageNum === 1 ? projects : [...prev, ...projects]
        );
        setTotal(t);
        setPage(pageNum);
        setLoading(false);
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadPage(1);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadPage]);

  const hasMore = games.length < total;

  return (
    <section className="rb-section rb-section--glow" id="games">
      <div className="rb-container">
        <Reveal as="span" className="rb-section__eyebrow">
          Каталог игр
        </Reveal>
        <Reveal as="h2" className="rb-section__title">
          <span className="rb-section__title-accent">Популярные</span> игры
        </Reveal>
        <Reveal as="p" className="rb-section__subtitle">
          Тысячи 3D-игр от сообщества Рублокса. Выбирай игру и запускай
          в приложении — это бесплатно.
        </Reveal>

        {error && games.length === 0 ? (
          /* Была строка «Попробуйте обновить страницу» — инструкция вместо
             действия. Теперь кнопка повторяет запрос на месте. */
          <EmptyState
            title="Не получилось загрузить игры"
            desc="Проверь соединение и попробуй ещё раз."
            action={
              <Button variant="primary" onClick={() => loadPage(1)}>
                Повторить
              </Button>
            }
          />
        ) : !loading && games.length === 0 ? (
          /* Пустого состояния не было вовсе: валидный ответ с пустым фидом
             давал белое пятно без объяснения. */
          <EmptyState
            title="Каталог пока пуст"
            desc="Игры появятся здесь, как только авторы их опубликуют."
          />
        ) : (
          <>
            <div className="rb-games__grid">
              {games.map((g, i) => (
                <Reveal
                  key={g.id}
                  variant="scale"
                  delay={Math.min((i % PER_PAGE) * 35, 420)}
                >
                  <GameCard game={g} onClick={setModalGame} />
                </Reveal>
              ))}
              {loading &&
                games.length === 0 &&
                Array.from({ length: PER_PAGE }).map((_, i) => (
                  <div key={i} className="rb-gamecard rb-gamecard--ph" />
                ))}
            </div>

            {/* Ошибка догрузки: список остаётся, сообщение рядом.
                Раньше ветка отображения требовала games.length === 0,
                поэтому провал второй страницы был полностью немым. */}
            {error && games.length > 0 && (
              <div className="rb-games__error">
                Не удалось загрузить продолжение.
              </div>
            )}

            {hasMore && (
              <div className="rb-games__more">
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => loadPage(page + 1)}
                  loading={loading}
                >
                  Показать ещё
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {modalGame && (
        <DownloadModal game={modalGame} onClose={() => setModalGame(null)} />
      )}
    </section>
  );
}
